import { Injectable, inject } from '@angular/core';

import { SimpleCallback } from '@frontend/sports/common/core/utils/extended-types';
import { generateGuid } from '@frontend/sports/common/core/utils/string';
import { Subscription } from 'rxjs';

import { TimerService } from './timer.service';

export interface TimerConfig {
    id: string;
    interval?: TickerInterval;
    onTick(): void;
}

export enum TickerInterval {
    Second = 'Second',
    Minute = 'Minute',
}

@Injectable({ providedIn: 'root' })
export class TickerService {
    private timerService = inject(TimerService);

    private timers: { [id: string]: TimerConfig } = {};
    private second = 1000;
    private minute = this.second * 60;
    private secondsInterval?: SimpleCallback;
    private minutesInterval?: SimpleCallback;

    subscribe(config: TimerConfig): void;
    subscribe(callback: () => void, interval?: TickerInterval): Subscription;
    subscribe(config: TimerConfig | (() => void), interval?: TickerInterval): Subscription {
        let timer: TimerConfig;

        if (config instanceof Function) {
            timer = {
                id: generateGuid(),
                onTick: config,
                interval: interval || TickerInterval.Second,
            };
        } else {
            timer = config;
            timer.interval = timer.interval || TickerInterval.Second;
        }

        this.timers[timer.id] = timer;
        this.runInterval();

        return new Subscription(() => this.unsubscribe(timer.id));
    }

    unsubscribe(id: string): void {
        this.unsubscribeInternal(id, this.timers);
    }

    private runInterval(): void {
        if (this.secondsInterval || this.minutesInterval) {
            return;
        }

        this.secondsInterval = this.timerService.setIntervalOutsideAngular(() => this.runIntervalType(TickerInterval.Second), this.second);
        this.minutesInterval = this.timerService.setIntervalOutsideAngular(() => this.runIntervalType(TickerInterval.Minute), this.minute);
    }

    private runIntervalType(interval: TickerInterval): void {
        const keysForInterval = Object.keys(this.timers).filter((id) => this.timers[id].interval === interval);

        if (!keysForInterval.length) {
            return;
        }

        keysForInterval.forEach((id) => this.timers[id].onTick());
    }

    private unsubscribeInternal(id: string, timers: { [id: string]: TimerConfig }): void {
        if (!timers[id]) {
            return;
        }

        // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-dynamic-delete
        delete timers[id];

        if (Object.keys(timers).length) {
            return;
        }

        if (this.secondsInterval) {
            this.secondsInterval();
            this.secondsInterval = undefined;
        }

        if (this.minutesInterval) {
            this.minutesInterval();
            this.minutesInterval = undefined;
        }
    }
}
